// ══════════════════════════════════════════════════════════════════════════════
// PAGE DOCUMENTS - Google Drive connecté
// AbracadaWorld - Cockpit Particulier
// ══════════════════════════════════════════════════════════════════════════════

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FolderOpen, File, FileText, Image, Film, Music, Archive,
  RefreshCw, Search, Grid, List, ExternalLink, Download,
  ChevronRight, Clock, HardDrive, LogOut, Folder, X,
  FileSpreadsheet, Presentation, Code, ArrowLeft
} from 'lucide-react';
import { toast } from 'sonner';
import RegisterPromptModal from '../../components/RegisterPromptModal';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const getToken = () => {
  const buyerToken = localStorage.getItem('buyer_token');
  if (buyerToken) return buyerToken;
  const clientToken = localStorage.getItem('client_token');
  if (clientToken) return clientToken;
  return localStorage.getItem('authToken');
};

// Mapping des types MIME vers icônes
const getFileIcon = (mimeType) => {
  if (!mimeType) return File;
  
  if (mimeType.includes('folder')) return Folder;
  if (mimeType.includes('document') || mimeType.includes('text')) return FileText;
  if (mimeType.includes('spreadsheet') || mimeType.includes('excel')) return FileSpreadsheet;
  if (mimeType.includes('presentation') || mimeType.includes('powerpoint')) return Presentation;
  if (mimeType.includes('image')) return Image;
  if (mimeType.includes('video')) return Film;
  if (mimeType.includes('audio')) return Music;
  if (mimeType.includes('zip') || mimeType.includes('archive')) return Archive;
  if (mimeType.includes('code') || mimeType.includes('javascript') || mimeType.includes('json')) return Code;
  
  return File;
};

// Couleurs par type de fichier
const getFileColor = (mimeType) => {
  if (!mimeType) return 'text-gray-500';
  
  if (mimeType.includes('folder')) return 'text-yellow-500';
  if (mimeType.includes('document')) return 'text-blue-500';
  if (mimeType.includes('spreadsheet')) return 'text-green-500';
  if (mimeType.includes('presentation')) return 'text-orange-500';
  if (mimeType.includes('image')) return 'text-pink-500';
  if (mimeType.includes('video')) return 'text-purple-500';
  if (mimeType.includes('audio')) return 'text-indigo-500';
  
  return 'text-gray-500';
};

// Composant FileIcon
function FileIcon({ mimeType, size = 20 }) {
  const Icon = getFileIcon(mimeType);
  const color = getFileColor(mimeType);
  return <Icon size={size} className={color} />;
}

export default function DocumentsPage({ onOpenRegisterModal, onBackToHub }) {
  const [googleStatus, setGoogleStatus] = useState({ connected: false, loading: true });
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState('list'); // 'list' ou 'grid'
  const [localShowRegisterModal, setLocalShowRegisterModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  
  // Détection mobile
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Vérifier la connexion Google
  const checkGoogleStatus = async () => {
    try {
      const token = getToken();
      if (!token) {
        setGoogleStatus({ connected: false, loading: false });
        return;
      }
      const res = await fetch(`${API_URL}/api/google/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setGoogleStatus({ ...data, loading: false });
        if (data.connected) {
          loadFiles();
        }
      } else {
        setGoogleStatus({ connected: false, loading: false });
      }
    } catch (error) {
      console.error('Error checking Google status:', error);
      setGoogleStatus({ connected: false, loading: false });
    }
  };

  // Connecter Google
  const connectGoogle = async () => {
    try {
      const token = getToken();
      
      if (!token) {
        // Ouvrir le popup d'inscription via le parent
        if (onOpenRegisterModal) {
          onOpenRegisterModal('accéder à ton Google Drive', true);
        } else {
          setLocalShowRegisterModal(true);
        }
        return;
      }
      
      const res = await fetch(`${API_URL}/api/google/auth/url`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const { auth_url } = await res.json();
        window.location.href = auth_url;
      } else {
        toast.error('Erreur de connexion Google');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    }
  };

  // Déconnecter Google
  const disconnectGoogle = async () => {
    if (!window.confirm('Déconnecter ton compte Google ?')) return;
    
    try {
      const token = getToken();
      await fetch(`${API_URL}/api/google/disconnect`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      setGoogleStatus({ connected: false, loading: false });
      setFiles([]);
      toast.success('Compte Google déconnecté');
    } catch (error) {
      toast.error('Erreur de déconnexion');
    }
  };

  // Charger les fichiers
  const loadFiles = async () => {
    setLoading(true);
    try {
      const token = getToken();
      const res = await fetch(`${API_URL}/api/google/drive/files?page_size=50`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setFiles(data.files || []);
      }
    } catch (error) {
      console.error('Error loading files:', error);
      toast.error('Erreur de chargement des fichiers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    checkGoogleStatus();
    
    // Check for callback
    const params = new URLSearchParams(window.location.search);
    if (params.get('google') === 'connected') {
      toast.success('Compte Google connecté !');
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const formatSize = (bytes) => {
    if (!bytes) return '-';
    const sizes = ['o', 'Ko', 'Mo', 'Go'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round(bytes / Math.pow(1024, i)) + ' ' + sizes[i];
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const filteredFiles = files.filter(file => {
    if (!searchQuery) return true;
    return file.name?.toLowerCase().includes(searchQuery.toLowerCase());
  });

  // État non connecté - Style harmonisé
  if (!googleStatus.loading && !googleStatus.connected) {
    return (
      <div className="h-full flex flex-col">
        {/* Header */}
        <div className={isMobile ? 'mb-2' : 'mb-4'}>
          <div className="flex items-center gap-2">
            {isMobile && onBackToHub && (
              <button onClick={onBackToHub} className="p-1 -ml-1" data-testid="back-to-hub-btn">
                <ArrowLeft size={20} className="text-[#1e3a5f]" strokeWidth={2} />
              </button>
            )}
            <div>
              <h1 className={`${isMobile ? 'text-[16px]' : 'text-xl'} font-bold text-[#1e3a5f]`}>Mes documents</h1>
              {!isMobile && <p className="text-sm text-gray-500">Classe et retrouve tes fichiers</p>}
            </div>
          </div>
        </div>

        {/* État non connecté */}
        <div className="flex-1 rounded-2xl bg-white border border-gray-100 flex items-center justify-center">
          <div className="text-center p-8 max-w-md">
            <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-orange-100 text-orange-700 rounded-full text-xs font-semibold mb-4">
              <span className="w-1.5 h-1.5 bg-orange-500 rounded-full animate-pulse" />
              Version Beta
            </div>
            
            <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4 bg-gradient-to-br from-orange-100 to-orange-50">
              <HardDrive size={28} className="text-orange-500" />
            </div>
            
            <h3 className="text-lg font-semibold mb-2 text-gray-900">Mes Documents</h3>
            <p className="text-sm mb-6 text-gray-500">
              Connecte ton cloud pour accéder à tes documents depuis ton cockpit.
            </p>
            
            {/* Google Drive */}
            <button
              onClick={connectGoogle}
              data-testid="connect-drive-btn"
              className="w-full py-3 rounded-xl flex items-center justify-center gap-3 transition-colors mb-3 bg-white hover:bg-gray-50 border-2 border-gray-200"
            >
              <img src="https://www.google.com/favicon.ico" alt="Google" className="w-5 h-5" />
              <span className="font-semibold text-sm text-gray-700">Google Drive</span>
              <span className="ml-auto px-2 py-0.5 bg-green-100 text-green-700 text-xs font-semibold rounded-full">Disponible</span>
            </button>
            
            {/* OneDrive */}
            <div className="w-full py-3 rounded-xl flex items-center justify-center gap-3 mb-3 opacity-60 bg-gray-50 border-2 border-dashed border-gray-200">
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none">
                <path d="M12 2L2 7v10l10 5 10-5V7L12 2z" fill="#0078D4"/>
              </svg>
              <span className="font-semibold text-sm text-gray-500">OneDrive</span>
              <span className="ml-auto px-2 py-0.5 bg-blue-100 text-blue-600 text-xs font-semibold rounded-full">Bientôt</span>
            </div>
            
            {/* Dropbox */}
            <div className="w-full py-3 rounded-xl flex items-center justify-center gap-3 opacity-60 bg-gray-50 border-2 border-dashed border-gray-200">
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="#0061FF">
                <path d="M6 2l6 3.75L6 9.5 0 5.75 6 2zm12 0l6 3.75-6 3.75-6-3.75L18 2zM0 13.25L6 9.5l6 3.75-6 3.75-6-3.75zm18-3.75l6 3.75-6 3.75-6-3.75 6-3.75zM6 18.5l6-3.75 6 3.75-6 3.75-6-3.75z"/>
              </svg>
              <span className="font-semibold text-sm text-gray-500">Dropbox</span>
              <span className="ml-auto px-2 py-0.5 bg-blue-100 text-blue-600 text-xs font-semibold rounded-full">Bientôt</span>
            </div>
            
            <p className="mt-4 text-xs text-gray-400">Tes données restent privées et sécurisées.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* Header - Style harmonisé */}
      <div className={isMobile ? 'mb-2' : 'mb-4'}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {isMobile && onBackToHub && (
              <button onClick={onBackToHub} className="p-1 -ml-1" data-testid="back-to-hub-btn">
                <ArrowLeft size={20} className="text-[#1e3a5f]" strokeWidth={2} />
              </button>
            )}
            <div>
              <h1 className="text-xl font-bold text-[#1e3a5f]">Mes documents</h1>
              {!isMobile && (
                <p className="text-sm text-gray-500">
                  {googleStatus.email || 'Classe et retrouve tes fichiers'}
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setViewMode(viewMode === 'list' ? 'grid' : 'list')}
              className="p-2 rounded-xl bg-white border border-gray-100 hover:bg-gray-50 transition-colors"
            >
              {viewMode === 'list' ? (
                <Grid size={18} className="text-gray-500" />
              ) : (
                <List size={18} className="text-gray-500" />
              )}
            </button>
            <button
              onClick={loadFiles}
              disabled={loading}
              className="p-2 rounded-xl bg-white border border-gray-100 hover:bg-gray-50 transition-colors"
            >
              <RefreshCw size={18} className={`text-gray-500 ${loading ? 'animate-spin' : ''}`} />
            </button>
            <button
              onClick={disconnectGoogle}
              className="p-2 rounded-xl bg-white border border-gray-100 hover:bg-gray-50 transition-colors"
              title="Déconnecter Google"
            >
              <LogOut size={18} className="text-gray-500" />
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="flex items-center gap-4 mt-3">
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white border border-gray-100">
            <HardDrive size={14} className="text-orange-500" />
            <span className="text-xs font-medium text-gray-600">
              {files.length} fichier{files.length > 1 ? 's' : ''}
            </span>
          </div>
        </div>
      </div>

      {/* Recherche */}
      <div className="mb-4">
        <div className="flex items-center gap-2 px-2.5 py-1.5 rounded-xl bg-gray-100">
          <Search size={14} className="text-gray-400 flex-shrink-0" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Rechercher un document..."
            className="flex-1 bg-transparent border-0 outline-none text-xs text-gray-900 placeholder-gray-400"
          />
        </div>
      </div>

      {/* Liste des fichiers */}
      <div className="flex-1 rounded-2xl bg-white border border-gray-100 overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : filteredFiles.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center p-8">
              <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4 bg-gray-50">
                <HardDrive size={28} className="text-gray-300" />
              </div>
              <h3 className="text-lg font-semibold mb-2 text-gray-900">Aucun document</h3>
              <p className="text-sm text-gray-500">Tes fichiers Google Drive apparaîtront ici.</p>
            </div>
          </div>
        ) : (
          <div className={viewMode === 'grid' ? 'grid grid-cols-2 md:grid-cols-4 gap-4 p-4' : 'divide-y divide-gray-100'}>
            {filteredFiles.map((file) => (
              <div
                key={file.id}
                className={viewMode === 'grid' 
                  ? 'p-4 rounded-xl border border-gray-100 hover:bg-gray-50 cursor-pointer'
                  : 'p-4 hover:bg-gray-50 cursor-pointer flex items-center gap-3'
                }
                onClick={() => file.webViewLink && window.open(file.webViewLink, '_blank')}
              >
                <div className={`flex items-center justify-center ${viewMode === 'grid' ? 'w-12 h-12 mx-auto mb-3' : 'w-10 h-10'} rounded-xl bg-orange-50`}>
                  <FileIcon mimeType={file.mimeType} size={viewMode === 'grid' ? 24 : 20} />
                </div>
                <div className={viewMode === 'grid' ? 'text-center' : 'flex-1 min-w-0'}>
                  <p className={`font-medium text-gray-800 ${viewMode === 'grid' ? 'text-sm truncate' : 'truncate'}`}>
                    {file.name}
                  </p>
                  <p className="text-xs text-gray-400">
                    {formatDate(file.modifiedTime)} • {formatSize(file.size)}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal d'inscription contextuel fallback (si parent ne gère pas) */}
      {!onOpenRegisterModal && (
        <RegisterPromptModal 
          isOpen={localShowRegisterModal} 
          onClose={() => setLocalShowRegisterModal(false)}
          actionLabel="accéder à tes documents Google Drive"
          showGoogleOption={true}
        />
      )}
    </div>
  );
}
